import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  Box, Typography, List, ListItem, ListItemText, ListItemAvatar, Avatar,
  Button, Stack, Chip, Divider, LinearProgress, Alert, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  Badge,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import DeleteIcon from '@mui/icons-material/Delete';
import SendIcon from '@mui/icons-material/Send';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import CampaignIcon from '@mui/icons-material/Campaign';
import {
  MY_NOTIFICATIONS_QUERY,
  MARK_NOTIFICATION_READ_MUTATION, MARK_ALL_NOTIFICATIONS_READ_MUTATION,
  BROADCAST_NOTIFICATION_MUTATION, CREATE_NOTIFICATION_MUTATION,
  DELETE_NOTIFICATION_MUTATION,
  USERS_QUERY,
} from '../graphql/queries';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

import Paged from '../components/Paged'; // lists beyond 10 are paged
const TYPE_ICON = {
  info:       <InfoIcon />,
  reminder:   <NotificationsIcon />,
  alert:      <WarningIcon />,
  emergency:  <CampaignIcon color="error" />,
  vendor:     <InfoIcon />,
  schedule:   <NotificationsIcon />,
  success:    <InfoIcon />,
  warning:    <WarningIcon />,
  action:     <NotificationsIcon />,
};
const TYPE_COLOR = { info:'primary', reminder:'warning', alert:'error', emergency:'error', vendor:'secondary', schedule:'info', success:'success', warning:'warning', action:'secondary' };

const EMPTY_NOTIF = { type:'info', title:'', message:'', targetRole:'' };

// Page that handles what a notification is about, for the viewer's role
function linkFor(n, roleName) {
  const isAdmin = roleName === 'system_admin';
  switch (n.relatedEntityType) {
    case 'vendor_selection': return roleName === 'vendor' ? '/dashboard/vendor-bookings' : '/dashboard/my-vendors';
    case 'contract':         return isAdmin ? '/dashboard/admin/contracts' : '/dashboard/contracts';
    case 'service_wishlist': return isAdmin ? '/dashboard/admin/wishlist-approvals' : '/dashboard/wishlist-wizard';
    default:                 return null;
  }
}

export default function NotificationsPage() {
  const { user: me } = useAuth();
  const navigate = useNavigate();
  const [showUnread, setShowUnread] = useState(false);
  const [sendDialog, setSendDialog] = useState(false);
  const [notifForm, setNotifForm]   = useState(EMPTY_NOTIF);
  const [error, setError]           = useState('');

  const { data, loading, refetch } = useQuery(MY_NOTIFICATIONS_QUERY, {
    variables: { unreadOnly: showUnread },
    fetchPolicy: 'cache-and-network',
  });
  const { data: usersData } = useQuery(USERS_QUERY, { skip: me?.role?.name !== 'system_admin', fetchPolicy: 'cache-and-network' });

  const notifications = data?.myNotifications || [];
  const unread = notifications.filter(n => !n.isRead).length;

  // Keep the notification bell's count in step
  const bell = { refetchQueries: ['MyUnreadNotifications'] };
  const [markRead]     = useMutation(MARK_NOTIFICATION_READ_MUTATION,     { ...bell, onCompleted: () => refetch() });
  const [markAllRead]  = useMutation(MARK_ALL_NOTIFICATIONS_READ_MUTATION,{ ...bell, onCompleted: () => refetch() });
  const [broadcast]    = useMutation(BROADCAST_NOTIFICATION_MUTATION,     { onCompleted: () => { setSendDialog(false); setNotifForm(EMPTY_NOTIF); setError(''); }, onError: e => setError(e.message) });
  const [deleteNotif]  = useMutation(DELETE_NOTIFICATION_MUTATION,        { ...bell, onCompleted: () => refetch() });

  const handleSend = () => {
    broadcast({ variables: { type: notifForm.type, title: notifForm.title, message: notifForm.message, targetRole: notifForm.targetRole || null } });
  };

  const isAdmin = me?.role?.name === 'system_admin';

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Badge badgeContent={unread} color="error">
              <NotificationsIcon color="primary" />
            </Badge>
            <Typography variant="h4" fontWeight={700}>Notifications</Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">Alerts, reminders, vendor updates, and emergency broadcasts.</Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          {unread > 0 && (
            <Button variant="outlined" startIcon={<DoneAllIcon />} onClick={() => markAllRead()}>Mark All Read</Button>
          )}
          {isAdmin && (
            <Button variant="contained" startIcon={<SendIcon />} onClick={() => setSendDialog(true)}>Send Notification</Button>
          )}
        </Stack>
      </Stack>

      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <Chip label="All" onClick={() => setShowUnread(false)} color={!showUnread ? 'primary' : 'default'} variant={!showUnread ? 'filled' : 'outlined'} />
        <Chip label={`Unread (${unread})`} onClick={() => setShowUnread(true)} color={showUnread ? 'primary' : 'default'} variant={showUnread ? 'filled' : 'outlined'} />
      </Stack>

      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {notifications.length === 0 && !loading && (
        <Alert severity="info">{showUnread ? 'No unread notifications.' : 'No notifications yet.'}</Alert>
      )}

      <List disablePadding>
        <Paged items={notifications}>{rows => rows.map((n, idx) => (
          <React.Fragment key={n.id}>
            <ListItem
              alignItems="flex-start"
              sx={{ bgcolor: n.isRead ? 'transparent' : 'action.hover', borderRadius: 1, mb: 0.5, cursor: !n.isRead ? 'pointer' : 'default' }}
              onClick={() => !n.isRead && markRead({ variables: { id: n.id } })}
              secondaryAction={
                <Tooltip title="Delete">
                  <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); deleteNotif({ variables: { id: n.id } }); }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              }
            >
              <ListItemAvatar>
                <Avatar sx={{ bgcolor: `${TYPE_COLOR[n.type] || 'primary'}.main` }}>
                  {TYPE_ICON[n.type] || <InfoIcon />}
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography fontWeight={n.isRead ? 400 : 700}>{n.title}</Typography>
                    <Chip label={n.type} size="small" color={TYPE_COLOR[n.type] || 'default'} />
                    {!n.isRead && <Chip label="New" size="small" color="error" />}
                  </Stack>
                }
                secondary={
                  <Box>
                    <Typography variant="body2" color="text.primary">{n.message}</Typography>
                    {/* A family's notifications reach both its contacts (the secondary by email) */}
                    {n.recipients?.length > 0 && (
                      <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{ my: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">To:</Typography>
                        {n.recipients.map(r => <Chip key={r} size="small" variant="outlined" label={r} sx={{ height: 20, fontSize: 11 }} />)}
                      </Stack>
                    )}
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="caption" color="text.secondary">
                        {n.sentAt ? new Date(n.sentAt).toLocaleString() : ''}{n.creator ? ` \u00b7 from ${n.creator.email}` : ''}
                      </Typography>
                      {linkFor(n, me?.role?.name) && (
                        <Button size="small" sx={{ py: 0 }} onClick={(e) => {
                          e.stopPropagation();
                          if (!n.isRead) markRead({ variables: { id: n.id } });
                          navigate(linkFor(n, me?.role?.name));
                        }}>
                          Open
                        </Button>
                      )}
                    </Stack>
                  </Box>
                }
              />
            </ListItem>
            {idx < notifications.length - 1 && <Divider variant="inset" component="li" />}
          </React.Fragment>
        ))}</Paged>
      </List>

      {/* Send Notification Dialog (admin) */}
      <Dialog open={sendDialog} onClose={() => setSendDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Broadcast Notification</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack direction="row" spacing={2}>
              <TextField select label="Type" value={notifForm.type} onChange={e => setNotifForm(f => ({ ...f, type: e.target.value }))} fullWidth>
                {['info','reminder','alert','emergency','vendor','schedule'].map(t => (
                  <MenuItem key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</MenuItem>
                ))}
              </TextField>
              <TextField select label="Target Role" value={notifForm.targetRole} onChange={e => setNotifForm(f => ({ ...f, targetRole: e.target.value }))} fullWidth>
                <MenuItem value="">All Users</MenuItem>
                {['bride_family','groom_family','matchmaker','vendor','system_admin'].map(r => (
                  <MenuItem key={r} value={r}>{r.replace(/_/g,' ').replace(/\b\w/g,c=>c.toUpperCase())}</MenuItem>
                ))}
              </TextField>
            </Stack>
            <TextField label="Title" value={notifForm.title} onChange={e => setNotifForm(f => ({ ...f, title: e.target.value }))} fullWidth required />
            <TextField label="Message" value={notifForm.message} onChange={e => setNotifForm(f => ({ ...f, message: e.target.value }))} fullWidth required multiline rows={3} />
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setSendDialog(false); setError(''); }}>Cancel</Button>
          <Button variant="contained" onClick={handleSend} startIcon={<SendIcon />} disabled={!notifForm.title || !notifForm.message}>Send</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
